<template>
  <z-skeleton-loader v-if="isLoading" type="table"></z-skeleton-loader>

  <z-groupable-table
    v-else-if="groupableColumns"
    v-bind="$props"
    v-on="$listeners"
  >
    <template
      v-if="$scopedSlots['groupable-header-filter']"
      #header-filter="slotProps"
    >
      <!--@slot Slot de customização do conteúdo do menu de filtro ao usar a prop `groupable-columns`
            @binding {object} header - header da coluna
      -->
      <slot name="groupable-header-filter" v-bind="slotProps"></slot>
    </template>

    <template v-for="(_, name) in $scopedSlots" v-slot:[name]="data">
      <slot :name="name" v-bind="data"></slot>
    </template>
  </z-groupable-table>

  <z-card v-else class="px-0 py-0" width="100%">
    <template #content>
      <z-pagination
        :class="{'no-padding': !autoPagination && !numberOfPages}"
        :auto-pagination="autoPagination"
        :items="appliedItems"
        :items-per-page="itemsPerPage"
        :current-page="currentPage"
        :icon-next="iconNext"
        :icon-previous="iconPrevious"
        :number-of-pages="numberOfPages"
        :show-items-per-page="showItemsPerPage"
        :items-per-page-options="itemsPerPageOptions"
        :items-per-page-text="itemsPerPageText"
        @change:items-per-page="$emit('change:items-per-page', +$event)"
        @change:page="onChangePage"
        @click:next="onClickNext"
        @click:previous="onClickPrevious"
      >
        <template #data="{paginatedItems}">
          <v-data-table
            ref="zooxTable"
            class="zoox-component zoox-table elevation-0"
            hide-default-footer
            hide-default-header
            :expanded="expandedItems"
            :items-per-page="itemsPerPage || items.length"
            :single-expand="singleExpand"
            :show-expand="expandable"
            :headers="appliedHeaders"
            :items="paginatedItems"
            :height="height"
            :fixed-header="fixedHeader"
            :class="{
              small,
              'fixed-header': fixedHeader,
            }"
            v-bind="$attrs"
            v-on="$listeners"
          >
            <template
              v-for="(headerItem, index) in appliedHeaders"
              v-slot:[`item.${headerItem.value}`]="{item}"
            >
              <z-overflow-tooltip
                :key="index"
                :content="item[headerItem.value] || ''"
              >
              </z-overflow-tooltip>
            </template>

            <!--I Repassa todos os slots para o vuetify -->
            <slot v-for="(_, name) in $slots" :slot="name" :name="name" />
            <template
              v-for="(_, name) in $scopedSlots"
              v-slot:[name]="slotData"
            >
              <slot :name="name" v-bind="slotData" />
            </template>

            <!-- Header customizado -->
            <template v-if="!hiddenHeader" #header="{props: {headers}}">
              <z-table-header
                :headers="headers"
                :styles="headerStyles"
                @sort="onSort"
              >
                <template
                  v-for="(_, name) in $scopedSlots"
                  v-slot:[name]="slotData"
                >
                  <slot :name="name" v-bind="slotData" />
                </template>
              </z-table-header>
            </template>

            <template #expanded-item="{headers, item}">
              <td class="td-expanded-item" :colspan="headers.length">
                <!--@slot Slot onde os itens expandiveis são adicionados 
                    @binding {any} item - Item que está sendo expandido
                -->
                <slot name="expanded" :item="item" />
              </td>
            </template>

            <template #item.data-table-expand="{item, isExpanded}">
              <z-icon @click="expandItem(item)">{{
                isExpanded
                  ? '$keyboard_arrow_up_rounded'
                  : '$keyboard_arrow_down_rounded'
              }}</z-icon>
            </template>
          </v-data-table>
        </template>

        <template v-if="$scopedSlots['pagination-actions']" #actions>
          <slot name="pagination-actions" />
        </template>
        <template v-if="$scopedSlots['pagination-precontrols']" #precontrols>
          <slot name="pagination-precontrols"></slot>
        </template>
      </z-pagination>
    </template>
  </z-card>
</template>

<script>
/**
 * @displayName Table
 */
export default {
  name: 'ZTable',

  props: {
    /**
     * Flag para indicar se é expandível
     */
    expandable: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Flag para indicar que o Header não deve ser exibido
     */
    hiddenHeader: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Headers da tabela
     *
     * @typedef {Object} {text:string:value:any,sortable:boolean}
     */
    headers: {
      type: Array,
      required: true,
    },
    /**
     * Itens da lista
     */
    items: {
      type: Array,
      required: true,
      validator: function (value) {
        const res = value.every((item) => Object.keys(item).includes('id'))

        if (res) {
          return true
        } else {
          console.error(
            'z-table: Os itens do array devem ser objetos e possuir a propriedade "id"'
          )
          return false
        }
      },
    },

    /**
     * Flag para fazer paginação automática
     */
    autoPagination: {
      type: Boolean,
      default: false,
    },
    /**
     * Quantide de itens por página (autoPagination)
     */
    itemsPerPage: {
      type: Number,
      required: false,
    },
    /**
     * Determina as opções de quantidade de itens por página
     */
    itemsPerPageOptions: {
      type: Array,
      default: () => [5, 10, 15, 20, 25, 50, 100],
    },
    /**
     * Flag para indicar ao componente mostrar a opção de quantidade de itens por página
     */
    showItemsPerPage: {
      type: Boolean,
      default: false,
    },
    /**
     * Texto a ser mostrado na opção de quantidade de itens por página
     */
    itemsPerPageText: {
      type: String,
      default: '',
    },
    /**
     * Define a página atual (ignorado com autopaging)
     */
    currentPage: {
      type: Number,
      required: false,
    },
    /**
     * Define a página atual (ignorado com autopaging)
     */
    numberOfPages: {
      type: Number,
      required: false,
    },
    /**
     * Flag para indicar ao componente o ícone que irá passar as páginas para direita.
     */
    iconNext: {
      type: String,
      default: '$keyboard_arrow_right_rounded',
    },
    /**
     * Flag para indicar ao componente o ícone que irá passar as páginas para esquerda.
     */
    iconPrevious: {
      type: String,
      default: '$keyboard_arrow_left_rounded',
    },

    /**
     * Aplica estilo de loading na tabela
     */
    isLoading: {
      type: Boolean,
      default: false,
    },
    /**
     * Flag para indicar que a tabela deve ser pequena
     */
    small: {
      type: Boolean,
      default: false,
    },
    /**
     * Indica que apenas um item pode ser expandido por vez
     */
    singleExpand: {
      type: Boolean,
      default: false,
    },
    /**
     * Ativa a funcionalidade de agrupamento de colunas da tabela
     */
    groupableColumns: {
      type: Boolean,
      default: false,
    },

    /**
     * Repassa as props  para o v-menu usado no slot de filtro
     */
    filterMenuProps: {
      type: Object,
      default: () => ({
        closeOnContentClick: false,
      }),
    },

    /**
     * Adiciona divisores verticais na tabela
     */
    verticalDividers: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Objeto de configuração dos estilos do header
     */
    headerStyles: {
      type: Object,
      required: false,
      default: () => ({}),
    },

    /**
     * Define uma altura explicita para a tabela
     */
    height: {
      type: [String, Number],
      required: false,
    },

    /**
     * Coloca o header da tabela fixo ao dar scroll (necessário definir uma altura para a tabela)
     */
    fixedHeader: {
      type: Boolean,
      required: false,
      default: false,
    },

    /**
     * @deprecated - apenas para compatibilidade, use o objeto de headers para definir as colunas fixas
     * Define a qual será a primeira coluna fixa da tabela
     */
    firstFixedColumn: {
      type: String,
      required: false,
      default: '',
    },

    /**
     * @deprecated - apenas para compatibilidade, use o objeto de headers para definir as colunas fixas
     * Define a qual será a última coluna fixa da tabela
     */
    lastFixedColumn: {
      type: String,
      required: false,
      default: '',
    },

    removeFixedShadows: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      expandedItems: [],
      isTdClamped: false,
      fixedColumnsElements: {
        left: [],
        right: [],
      },
    }
  },

  computed: {
    appliedItems() {
      if (!this.items.every((item) => Object.keys(item).includes('id'))) {
        return this.items.map((item, index) => ({
          ...item,
          id: index,
        }))
      } else {
        return this.items
      }
    },
    appliedHeaders() {
      let headers = this.headers.map((header) => ({
        sortable:
          typeof header.sortable === 'undefined' ? false : header.sortable,
        ...header,
      }))

      if (this.expandable) {
        headers.unshift({text: '', value: 'data-table-expand'})
      }

      /**------------------------------------------------ */
      /**
       * !! DEPRECIADO !!
       * Mantemos apenas para compatibilidade com a versão anterior
       * aplica as colunas fixas quando as props firstFixedColumn e lastFixedColumn são definidas
       */
      const firstFixedColumnIndex = headers.findIndex(
        (h) => h.value === this.firstFixedColumn
      )

      const lastFixedColumnIndex = headers.findIndex(
        (h) => h.value === this.lastFixedColumn
      )

      if (firstFixedColumnIndex > -1) {
        headers[firstFixedColumnIndex].fixed = true
        headers[firstFixedColumnIndex].position = 'left'
      }

      if (lastFixedColumnIndex > -1) {
        headers[lastFixedColumnIndex].fixed = true
        headers[lastFixedColumnIndex].position = 'right'
      }
      /**------------------------------------------------ */

      headers.sort((a, b) => {
        if (a.position === 'left' || b.position === 'right') {
          return -1
        }
        if (b.position === 'left' || a.position === 'right') {
          return 1
        }
        return 0
      })

      return headers
    },
  },

  watch: {
    appliedItems: {
      deep: true,
      immediate: true,
      handler() {
        this.$nextTick(() => {
          this.addFixedColumns({resetScroll: true})
          this.addDividers()
        })
      },
    },

    appliedHeaders: {
      deep: true,
      immediate: true,
      handler() {
        this.$nextTick(() => {
          this.addFixedColumns({resetScroll: true})
          this.addDividers()
        })
      },
    },

    isLoading(newVal) {
      if (!newVal) {
        this.$nextTick(() => {
          this.addFixedColumns()
          this.addDividers()
        })
      }
    },
  },

  mounted() {
    this.handleTableScrollWithFixedColumns()
  },

  methods: {
    onSort(column) {
      /**
       * Disparado quando é clicado para ordenar por uma
       * coluna específica
       *
       * @property {Object} column - Informações da coluna
       */
      this.$emit('sort', column)
    },
    onClickPrevious() {
      /**
       * Disparado quando clicado no botão de previous
       */
      this.$emit('previous')
    },
    onClickNext() {
      /**
       * Disparado quando clicado no botão de next
       */
      this.$emit('next')
    },
    onChangePage(page) {
      /**
       * Evento de alteração de página pelo select
       */
      this.$emit('change:page', page)

      this.$nextTick(() => {
        this.addFixedColumns()
        this.addDividers()
      })
    },

    expandItem(item) {
      const indexExpanded = this.expandedItems.findIndex((i) => i === item)

      if (indexExpanded > -1) {
        this.expandedItems.splice(indexExpanded, 1)
      } else {
        this.expandedItems.push(item)
      }
    },

    addDividers() {
      if (this.verticalDividers && this.$refs.zooxTable) {
        this.$refs.zooxTable.$el
          .querySelectorAll(
            `table > tbody > tr > td,
          table > thead > tr > th`
          )
          .forEach((element) => element.classList.add('vertical-divider'))
      }
    },

    /**
     * @public
     * Realiza os calculos necessários para adicionar as colunas fixas
     */
    async addFixedColumns({resetScroll = false} = {}) {
      if (this.$refs.zooxTable && this.appliedHeaders.some((h) => h.fixed)) {
        if (resetScroll) {
          this.$refs.zooxTable.$el.querySelector(
            '.v-data-table__wrapper'
          ).scrollLeft = 0
        }

        //valor do scroll horizontal da tabela
        await this.$nextTick()
        const scrolledValue = this.$refs.zooxTable.$el.querySelector(
          '.v-data-table__wrapper'
        ).scrollLeft

        //separa os headers fixos da esquerda e da direita
        const leftHeaders = []
        const rightHeaders = []

        this.appliedHeaders.forEach((header, index) => {
          if (header.fixed) {
            //seleciona os elementos que serão fixados
            const elements = this.$refs.zooxTable.$el.querySelectorAll(
              `table > tbody > tr > td:nth-child(${index + 1}),
          table > thead > tr > th:nth-child(${index + 1})`
            )

            //adiciona os elementos fixos no array correspondente
            if (header.position === 'left') {
              leftHeaders.push(elements)
            } else {
              rightHeaders.push(elements)
            }

            //itera sobre os elementos fixos (TH e TD)
            elements.forEach((el) => {
              el.classList.add('fixed')

              if (header.position === 'left') {
                //se for um header, adiciona o valor do scroll no offset
                if (el.tagName === 'TH') {
                  //z-index das THs fixas precisa ser maior que o das THs normais quando a prop fixed-header é usada
                  el.style.zIndex = 5
                  el.style.left = `${el.offsetLeft - scrolledValue}px`
                } else {
                  el.style.left = `${el.offsetLeft}px`
                }
              } else {
                if (el.tagName === 'TH') {
                  el.style.zIndex = 5
                }
                /**
                 * iteramos de trás para frente os elementos fixos da direita,
                 * adicionando a width do elemento anterior no offset do elemento atual
                 */
                let offset = 0
                for (let i = rightHeaders?.length - 1; i >= 0; i--) {
                  const elements = rightHeaders[i]
                  elements.forEach((el) => {
                    el.style.right = `${offset - 1}px`
                  })
                  offset += elements[0].offsetWidth
                }
              }
            })
          }
        })

        //aplica o box-shadow nos últimos elementos fixos da esquerda e no primeiro da direita
        if (!this.removeFixedShadows) {
          const lastLeftColumn = leftHeaders[leftHeaders?.length - 1]
          const firstRightColumn = rightHeaders[0]
          this.fixedColumnsElements = {
            left: lastLeftColumn,
            right: firstRightColumn,
          }

          if (lastLeftColumn?.length) {
            lastLeftColumn.forEach((el) => {
              el.style.boxShadow = 'rgba(0, 0, 0, 0.2) -6px 0px 8px -6px inset'
            })
          }

          if (firstRightColumn?.length) {
            firstRightColumn.forEach((el) => {
              el.style.boxShadow = 'rgba(0, 0, 0, 0.2) 6px 0px 8px -6px inset'
            })
          }
        }
      }
    },

    handleTableScrollWithFixedColumns() {
      if (this.$refs.zooxTable && this.appliedHeaders.some((h) => h.fixed)) {
        const scrollableContainer = this.$refs.zooxTable.$el.querySelector(
          '.v-data-table__wrapper'
        )

        if (scrollableContainer) {
          scrollableContainer.addEventListener('scroll', () => {
            if (scrollableContainer.scrollLeft === 0) {
              this.fixedColumnsElements.left.forEach((el) => {
                el.style.boxShadow = 'none'
              })
            } else {
              this.fixedColumnsElements.left.forEach((el) => {
                el.style.boxShadow =
                  'rgba(0, 0, 0, 0.2) -6px 0px 8px -6px inset'
              })
            }

            if (
              scrollableContainer.scrollLeft +
                scrollableContainer.clientWidth ===
              scrollableContainer.scrollWidth
            ) {
              this.fixedColumnsElements.right.forEach((el) => {
                el.style.boxShadow = 'none'
              })
            } else {
              this.fixedColumnsElements.right.forEach((el) => {
                el.style.boxShadow = 'rgba(0, 0, 0, 0.2) 6px 0px 8px -6px inset'
              })
            }
          })
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
:deep(.fixed) {
  background-color: white !important;
  position: sticky !important;
  z-index: 3;
  top: 0;
  border-bottom: thin solid rgba(0, 0, 0, 0.12) !important;
}

//adiciona background nas tds fixas quando há hover na TR pai
:deep(
    .theme--light.v-data-table
      > .v-data-table__wrapper
      > table
      > tbody
      > tr:hover:not(.v-data-table__expanded__content):not(
        .v-data-table__empty-wrapper
      )
      > td.fixed,

  ) {
  background: #eee !important;
}

:deep(
    .v-data-table--fixed-header
      > .v-data-table__wrapper
      > table
      > thead
      > tr
      > th
  ) {
  box-shadow: none;
  border-bottom: thin solid rgba(0, 0, 0, 0.12) !important;
}
</style>

<style lang="scss">
.zoox-table {
  width: 100%;
  min-width: 200px;
  border-radius: var(--component-card-radius);

  &.fixed-header > .v-data-table__wrapper > table > thead > tr > th {
    z-index: 4;
  }

  ::-webkit-scrollbar {
    width: 7px;
    height: 7px;
  }

  ::-webkit-scrollbar-thumb {
    background: var(--black-100);
    border-radius: 15px;
  }

  scrollbar-color: var(--black-100) var(--white); /* firefox */

  &.small {
    th,
    thead,
    td {
      height: 40px !important;
      font-size: var(--zoox-font-size-body);
    }
  }

  .vertical-divider {
    border-right: thin solid rgba(0, 0, 0, 0.12);
  }

  .td-expanded-item {
    background: var(--white) !important;
  }

  .v-data-table-header {
    height: 55px;
    text-transform: uppercase;
  }
}

.no-padding {
  padding-bottom: 0px !important;
}
</style>
